import { useState, useEffect, useCallback, useRef } from 'react';
import { STORAGE_KEY, URL_PARAMS_KEY } from '../config/constants';
import { getUserID, regenerateUserID } from '../services/userId';

function parseUrlParams() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);
    for (const [key, value] of searchParams.entries()) {
        params[key] = value;
    }
    return params;
}

export function usePersistence() {
    const [formData, setFormData] = useState(new Map());
    const [globalAnswers, setGlobalAnswers] = useState(new Map());
    const [userID, setUserID] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [urlParams, setUrlParams] = useState({});
    const [buttonLoadingStates, setButtonLoadingStates] = useState(new Map());

    const globalAnswersRef = useRef(globalAnswers);
    useEffect(() => {
        globalAnswersRef.current = globalAnswers;
    }, [globalAnswers]);

    // Init URL params
    useEffect(() => {
        try {
            const currentParams = parseUrlParams();
            const storedParamsJson = localStorage.getItem(URL_PARAMS_KEY);
            let storedParams = {};

            if (storedParamsJson) {
                storedParams = JSON.parse(storedParamsJson);
            }

            const mergedParams = { ...storedParams, ...currentParams };

            if (Object.keys(mergedParams).length > 0) {
                setUrlParams(mergedParams);
                localStorage.setItem(URL_PARAMS_KEY, JSON.stringify(mergedParams));
            }
        } catch (error) {
            console.error('Failed to initialize URL params:', error);
        }
    }, []);

    // Init userID: reuse the stored one, generate only if missing
    useEffect(() => {
        setUserID(getUserID());
    }, []);

    const saveGlobalAnswersToStorage = useCallback(() => {
        try {
            const answersObject = Object.fromEntries(globalAnswers);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(answersObject));
        } catch (error) {
            console.error('Failed to save answers to localStorage:', error);
        }
    }, [globalAnswers]);

    const loadGlobalAnswersFromStorage = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const answersObject = JSON.parse(stored);
                const answersMap = new Map(Object.entries(answersObject));
                globalAnswersRef.current = answersMap;
                setGlobalAnswers(answersMap);

                const newFormData = new Map();
                Object.entries(answersObject).forEach(([, answer]) => {
                    if (answer.elementName && answer.value) {
                        newFormData.set(answer.elementName, answer.value);
                    }
                });
                setFormData(newFormData);
            }
        } catch (error) {
            console.error('Failed to load answers from localStorage:', error);
        }
    };

    const updateGlobalAnswer = useCallback((screenName, answer) => {
        const newAnswers = new Map(globalAnswersRef.current);
        newAnswers.set(screenName, answer);
        globalAnswersRef.current = newAnswers;
        setGlobalAnswers(newAnswers);
    }, []);

    const updateFormData = useCallback((key, value) => {
        setFormData(prev => {
            const newData = new Map(prev);
            newData.set(key, value);
            return newData;
        });
    }, []);

    // Auto-save
    useEffect(() => {
        if (globalAnswers.size > 0) {
            saveGlobalAnswersToStorage();
        }
    }, [globalAnswers, saveGlobalAnswersToStorage]);

    const updateSelectedPlan = useCallback((plan) => {
        setSelectedPlan(plan);
    }, []);

    const setButtonLoading = useCallback((buttonHandler, isLoading) => {
        setButtonLoadingStates(prev => {
            const newMap = new Map(prev);
            if (isLoading) {
                newMap.set(buttonHandler, true);
            } else {
                newMap.delete(buttonHandler);
            }
            return newMap;
        });
    }, []);

    const resetSession = useCallback(() => {
        const newUserID = regenerateUserID();
        localStorage.removeItem('paymentProvider');
        localStorage.removeItem('customerID');
        setUserID(newUserID);
    }, []);

    const clearAllData = useCallback(() => {
        setGlobalAnswers(new Map());
        globalAnswersRef.current = new Map();
        setFormData(new Map());
        setSelectedPlan(null);
        localStorage.clear();
    }, []);

    return {
        formData,
        globalAnswers,
        globalAnswersRef,
        userID,
        selectedPlan,
        urlParams,
        buttonLoadingStates,
        loadGlobalAnswersFromStorage,
        updateGlobalAnswer,
        updateFormData,
        updateSelectedPlan,
        setButtonLoading,
        resetSession,
        clearAllData,
    };
}
